import { Box } from '@aicon-dev/caas-core';
import { useQuery } from '@tanstack/react-query';
import { Skeleton } from 'antd-mobile';
import { TFunction } from 'i18next';
import { Navigate } from 'react-big-calendar';
import { useTranslation } from 'react-i18next';

import { CalendarEmptyIcon } from '~/assets/icons';
import apiClient from '~/config/apiClient';

import { useProjectContext } from '../projects/context/ProjectProvider';

import { useCalendarDetailStore } from './store';
import { CalendarDataType } from './types';

interface EmptyProps {
  t: TFunction;
}

const Empty = (props: EmptyProps) => {
  const { t } = props;
  return (
    <Box
      display={'flex'}
      alignItems={'center'}
      justifyContent={'center'}
      flexDirection={'column'}
      pt="20%"
    >
      <CalendarEmptyIcon />
      <Box color={'#939393'} fontSize={'16px'} fontWeight={400} mt="10px">
        {t('schedule:calendar_empty_msg')}
      </Box>
    </Box>
  );
};

const CalendarDayView = (props: any) => {
  const { project } = useProjectContext();

  const { setVisible, setCurItem } = useCalendarDetailStore();

  const { t } = useTranslation('schedule');

  const fetchData = async () => {
    const params = {
      project_id: project?._id,
      date: props.date.toISOString(),
    };
    try {
      const { data } = await apiClient.get<CalendarDataType[]>('/schedules/calendar/daily', {
        params,
      });
      return data.map((el) => {
        return {
          ...el,
          process_id: el._id,
          tasks: el.tasks?.map((task) => {
            return { ...task, title: task.text, color: el.color };
          }),
        };
      });
    } catch (e) {
      console.log(e);
    }
  };
  const { data, isLoading } = useQuery({
    queryKey: ['fetchDailyCalendar', props.date],
    queryFn: fetchData,
  });

  if (isLoading) return <Skeleton.Paragraph lineCount={10} animated />;
  return (
    <Box px="4px">
      {data && data.length ? (
        data.map((el) => {
          return (
            <Box key={el._id} mb="16px">
              <Box
                background={el.color}
                mb="4px"
                borderRadius={'2px'}
                px="8px"
                color="#FFFFFF"
                fontWeight={400}
                fontSize={'16px'}
                display={'flex'}
                alignItems={'center'}
                height="36px"
                onClick={(e) => {
                  e.stopPropagation();
                  setCurItem(el);
                  setVisible(true);
                }}
              >
                {el.title}
              </Box>
              {el.tasks && el.tasks.length
                ? el.tasks.map((task: any) => {
                    return (
                      <Box
                        key={task._id}
                        mb="4px"
                        borderRadius={'2px'}
                        fontWeight={400}
                        display={'flex'}
                        alignItems={'center'}
                        color={'#505050'}
                        overflow={'hidden'}
                        fontSize={'16px'}
                        position={'relative'}
                        height={'36px'}
                        onClick={(e) => {
                          e.stopPropagation();
                          setCurItem({ ...task, process_id: el._id });
                          setVisible(true);
                        }}
                      >
                        <Box
                          minWidth={'4px'}
                          flex={1}
                          position={'absolute'}
                          height="100%"
                          background={el.color}
                        ></Box>
                        <Box
                          background={el.color + '1a'}
                          width={'100%'}
                          height={'100%'}
                          display={'flex'}
                          alignItems={'center'}
                          pl="12px"
                        >
                          {task.text}
                        </Box>
                      </Box>
                    );
                  })
                : null}
            </Box>
          );
        })
      ) : (
        <Empty t={t} />
      )}
    </Box>
  );
};

CalendarDayView.navigate = (date: any, action: any, { localizer }: any) => {
  switch (action) {
    case Navigate.PREVIOUS:
      return localizer.add(date, -1, 'day');

    case Navigate.NEXT:
      return localizer.add(date, 1, 'day');

    default:
      return date;
  }
};

CalendarDayView.title = (date: any) => {
  return date;
};

export default CalendarDayView;
